import {
  Button,
  Combobox,
  DescriptionList,
  InputField,
  RadioGroup,
  RichTextEditor,
  SectionCard,
  Select,
  SelectableCard,
  SelectableCardGrid,
} from '@enamelai/design-system'
import {
  AmountType,
  BillingPaymentNotePayload,
  InvoiceEntry,
  PaymentType,
} from '@dataTypes/billing.types'
import { type SavedCardDisplay } from '@services/api/billing/payment-method.api'
import { formatCurrency, formatInvoiceNumber } from '@utils/billing.utils'
import React, { useEffect, useMemo, useRef, useState } from 'react'
import { Col, Row } from 'react-bootstrap'
import './PaymentDetailsLayout.css'

interface PaymentDetailsLayoutProps {
  selectedInvoices: InvoiceEntry[]
  paymentDetails: {
    method: PaymentType
    setMethod: (method: PaymentType) => void
    date: string
    amountType: AmountType
    setAmountType: (type: AmountType) => void
    amount: number
    setAmount: (amount: number) => void
    amountDraft: string | null
    payor: string
    setPayor: (payor: string) => void
    notes: BillingPaymentNotePayload[]
    setNotes: (notes: BillingPaymentNotePayload[]) => void
    allPatients: unknown[]
    loadingPatients: boolean
    payorSelectOptions: Array<{ value: string; label: string }>
    selectedWithPaid: Array<
      InvoiceEntry & { amountPaid: number; amountDue: number }
    >
    totalAmountDue: number
    remainingAmount: number
    isFullyPaid: boolean
    selectedPayorOption: { value: string; label: string } | undefined
    handleUpdateNotes: (data: BillingPaymentNotePayload[]) => void
    handleSubmit: () => void
    handleAmountTypeChangeFull: () => void
    handleAmountTypeChangePartial: () => void
    handleAmountChange: (e: React.ChangeEvent<HTMLInputElement>) => void
    handleAmountBlur: (e: React.FocusEvent<HTMLInputElement>) => void
    isAmountInvalid: boolean
    savedCards: SavedCardDisplay[]
    loadingSavedCards: boolean
    selectedPaymentMethodId: string | null
    setSelectedPaymentMethodId: (id: string | null) => void
    isCardProcessing: boolean
  }
}

const PAYMENT_METHOD_OPTIONS = [
  { value: PaymentType.CASH, label: 'Cash' },
  { value: PaymentType.CARD, label: 'Card' },
]

/** Design-system date fields parse `MM / dd / yyyy`, not ISO. */
const formatPaymentDateDisplay = (isoDate: string): string => {
  const [year, month, day] = isoDate.split('-')
  if (!year || !month || !day) {
    return ''
  }
  return `${month} / ${day} / ${year}`
}

const PaymentDetailsLayout: React.FC<PaymentDetailsLayoutProps> = ({
  paymentDetails,
}) => {
  const {
    method,
    setMethod,
    date,
    amountType,
    amount,
    amountDraft,
    payor,
    setPayor,
    notes,
    selectedWithPaid,
    totalAmountDue,
    isFullyPaid,
    payorSelectOptions,
    loadingPatients,
    handleUpdateNotes,
    handleSubmit,
    handleAmountTypeChangeFull,
    handleAmountTypeChangePartial,
    handleAmountChange,
    handleAmountBlur,
    isAmountInvalid,
    savedCards,
    loadingSavedCards,
    selectedPaymentMethodId,
    setSelectedPaymentMethodId,
    isCardProcessing,
  } = paymentDetails

  const isCardMethod = method === PaymentType.CARD
  const isSubmitDisabled =
    isFullyPaid ||
    !payor ||
    isAmountInvalid ||
    isCardProcessing ||
    (isCardMethod && !selectedPaymentMethodId)

  const noteValue =
    typeof notes[0]?.body === 'string'
      ? notes[0].body
      : typeof notes[0]?.content === 'string'
        ? notes[0].content
        : ''
  const [payorQuery, setPayorQuery] = useState('')
  const payorEditRef = useRef(false)
  const dateDisplay = useMemo(() => formatPaymentDateDisplay(date), [date])

  useEffect(() => {
    if (payorEditRef.current) {
      payorEditRef.current = false
      if (!payor) return
    }

    const label = payorSelectOptions.find(
      (option) => option.value === payor,
    )?.label
    setPayorQuery(label ?? '')
  }, [payor, payorSelectOptions])

  const handlePayorChange = (value: string): void => {
    payorEditRef.current = true
    setPayorQuery(value)
    const match = payorSelectOptions.find((option) => option.value === value)
    setPayor(match?.value ?? '')
  }

  const savedCardOptions = savedCards.filter((card) => card.payment_method_id)

  return (
    <SectionCard title="Payment Details" className="payment-details-card">
      <div>
        <div className="fw-bold mb-2">Summary</div>
        {selectedWithPaid.length === 0 ? (
          <div>Select an invoice to record a payment.</div>
        ) : (
          <DescriptionList
            variant="file-details"
            items={[
              ...selectedWithPaid.map((invoice) => ({
                label: `Invoice # ${formatInvoiceNumber(invoice.invoiceNo)}`,
                value: formatCurrency(invoice.amountDue),
              })),
              {
                label: 'Total',
                value: formatCurrency(totalAmountDue),
              },
            ]}
          />
        )}
      </div>

      <Row className="gx-2">
        <Col>
          <RadioGroup
            name="amountType"
            label="Amount Type"
            orientation="horizontal"
            value={amountType}
            options={[
              { value: AmountType.Full, label: 'Full' },
              { value: AmountType.Partial, label: 'Partial' },
            ]}
            onChange={(value) => {
              if (value === AmountType.Full) {
                handleAmountTypeChangeFull()
                return
              }
              handleAmountTypeChangePartial()
            }}
          />
        </Col>
        <Col>
          <InputField
            className="payment-details-field"
            fullWidth
            label="Amount"
            variant="amount"
            clearable={false}
            disabled={amountType === AmountType.Full}
            value={amountDraft !== null ? amountDraft : amount || ''}
            error={isAmountInvalid}
            helperText={
              isAmountInvalid
                ? `Amount cannot exceed ${formatCurrency(totalAmountDue)}`
                : undefined
            }
            onChange={(event) => {
              handleAmountChange(event as React.ChangeEvent<HTMLInputElement>)
            }}
            onBlur={(event) => {
              handleAmountBlur(event as React.FocusEvent<HTMLInputElement>)
            }}
          />
        </Col>
      </Row>

      <Combobox
        className="payment-details-field"
        label="Payor Name"
        options={payorSelectOptions}
        value={payorQuery}
        allowCustom={false}
        disabled={loadingPatients}
        placeholder={
          loadingPatients ? 'Loading patients...' : 'Search or select payor'
        }
        onChange={handlePayorChange}
      />

      <Row className="gx-2">
        <Col>
          <Select
            className="payment-details-field"
            label="Payment Method"
            options={PAYMENT_METHOD_OPTIONS}
            value={method}
            disabled={isCardProcessing}
            onChange={(value) => {
              if (typeof value === 'string') {
                setMethod(value as PaymentType)
              }
            }}
          />
        </Col>
        <Col>
          <InputField
            className="payment-details-field"
            fullWidth
            label="Date"
            variant="date"
            value={dateDisplay}
            disabled
          />
        </Col>
      </Row>

      {isCardMethod ? (
        <div>
          <div>Saved Payment Methods</div>
          {!payor ? (
            <div>Select a payor to see saved cards.</div>
          ) : loadingSavedCards ? (
            <div>Loading saved cards...</div>
          ) : savedCardOptions.length === 0 ? (
            <div>No saved cards on file for this payor.</div>
          ) : (
            <SelectableCardGrid minColumnWidth={260}>
              {savedCardOptions.map((card) => {
                const id = card.payment_method_id as string
                return (
                  <SelectableCard
                    key={id}
                    title={`Card ending in ${card.lastFour}`}
                    subtitle={card.brand}
                    meta={`Valid till ${card.expiryMonth}/${card.expiryYear}`}
                    tags={card.isDefault ? ['Default'] : undefined}
                    selected={selectedPaymentMethodId === id}
                    disabled={isCardProcessing}
                    onClick={() => {
                      if (!isCardProcessing) {
                        setSelectedPaymentMethodId(id)
                      }
                    }}
                  />
                )
              })}
            </SelectableCardGrid>
          )}
        </div>
      ) : null}

      <Row>
        <Col>
          <RichTextEditor
            className="payment-details-notes"
            variant="sms-notes"
            value={noteValue}
            bodyActions={[]}
            footerActions={[]}
            aiEnabled={false}
            onChange={(value) => {
              const trimmed = value.trim()
              handleUpdateNotes(
                trimmed.length > 0 ? [{ body: value, content: value }] : [],
              )
            }}
          />
        </Col>
      </Row>

      <Row className="justify-content-end">
        <Col xs="auto">
          <Button
            variant="primary"
            onClick={handleSubmit}
            disabled={isSubmitDisabled}
            loading={isCardProcessing}
          >
            Submit
          </Button>
        </Col>
      </Row>
    </SectionCard>
  )
}

export default PaymentDetailsLayout
